/**
 * Single source of truth for tier display metadata — labels, accent tokens,
 * audit-only backing model names, upsell copy. Every UI surface that renders tier info
 * (switcher, history badge, ledger badge, upsell modal) reads from here so
 * the visual language stays coherent.
 *
 * Peak model name + upsell pitch are RESOLVED FROM the model registry at
 * read time — swapping `line_pro` from Sonnet to Gemini 2.5 Pro in
 * `aiModelRegistry.json` (or via env override) instantly re-labels the badge
 * from "Claude Sonnet 4.5" to "Gemini 2.5 Pro" with no code edit here.
 *
 * See docs/pricing/line-tier-plan.md §2 (backing models) and
 * docs/pricing/line-tier-integration-plan.md §7 + Phase J (registry).
 */

import type { AiModelId, AiTierId } from '../types/ai'
import type { AiBillingModel } from './aiBilling'
import {
  getModelSpec,
  getTierPeakModel,
  type AiModelSpec,
} from '../config/aiModelRegistry'

export interface AiTierDescriptor {
  id: AiTierId
  /** Short label rendered inside the switcher chip and history badge. */
  label: string
  /** Long label used in tooltips + response header ("Drafted by …"). */
  longLabel: string
  /** Internal/audit name of the backing model. Do not render to members. */
  modelName: string
  /**
   * Model id currently assigned to the tier's peak — used for the switcher's
   * cost preview and as an audit hint. Actual per-call routing is decided by
   * `aiRouter.selectModel` reading the same registry.
   */
  peakModel: AiModelId & AiBillingModel
  /** CSS class fragment used to key the accent color. Matches CSS tokens. */
  accentKey: 'line' | 'pro' | 'max'
  /** Short pitch shown inside the upsell modal header. */
  upsellPitch: string
  /** Provider of the current peak — useful for audit surfaces. */
  provider: AiModelSpec['provider']
  /** True when the current peak is covered by the GCP GenAI trial credit. */
  gcpTrialEligible: boolean
}

const STATIC_TIER_CHROME: Record<AiTierId, { label: string; accentKey: 'line' | 'pro' | 'max'; upsellPitchTemplate: string }> = {
  line: { label: 'Line', accentKey: 'line', upsellPitchTemplate: 'The everyday default — fast, cheap, always available.' },
  line_pro: { label: 'Line Pro', accentKey: 'pro', upsellPitchTemplate: 'Higher-quality reasoning for structural drafts and edits.' },
  line_max: { label: 'Line Max', accentKey: 'max', upsellPitchTemplate: 'Top-tier reasoning for the heaviest planning work.' },
}

const buildDescriptor = (tier: AiTierId): AiTierDescriptor => {
  const chrome = STATIC_TIER_CHROME[tier]
  const peakId = getTierPeakModel(tier)
  const spec = getModelSpec(peakId)
  const modelName = spec?.displayName ?? peakId
  return {
    id: tier,
    label: chrome.label,
    longLabel: chrome.label,
    modelName,
    peakModel: peakId as AiModelId & AiBillingModel,
    accentKey: chrome.accentKey,
    upsellPitch: chrome.upsellPitchTemplate,
    provider: spec?.provider ?? 'google',
    gcpTrialEligible: spec?.gcpTrialEligible ?? false,
  }
}

/**
 * Rebuild every render because `AI_TIER_CATALOG` is a getter — env overrides
 * flip results without a page reload once the module re-evaluates on next
 * `import()`. In practice React re-renders read this once per component
 * lifetime; the cost is negligible.
 */
export const AI_TIER_CATALOG: Record<AiTierId, AiTierDescriptor> = {
  get line() { return buildDescriptor('line') },
  get line_pro() { return buildDescriptor('line_pro') },
  get line_max() { return buildDescriptor('line_max') },
}

export const TIER_ORDER: readonly AiTierId[] = ['line', 'line_pro', 'line_max'] as const

export const isAiTierId = (raw: unknown): raw is AiTierId =>
  raw === 'line' || raw === 'line_pro' || raw === 'line_max'

/**
 * Map a persisted model id back to its owning tier for the history badge.
 * Uses provider + capability from the registry so newly-added models fall
 * into the right bucket without editing this file.
 */
export const tierForModel = (
  model: AiModelId | undefined,
  hint: AiTierId | undefined = undefined,
): AiTierId => {
  if (!model) return hint ?? 'line'
  const spec = getModelSpec(model)
  if (spec?.provider === 'anthropic') {
    // Any Claude in the registry maps to Pro-or-Max — pick by capability tag.
    return spec.capability === 'premium' ? 'line_max' : 'line_pro'
  }
  // Gemini models fall back to the hint (they can serve any tier depending on
  // the current tierAssignments). No hint → treat as Line, the safest default.
  return hint ?? 'line'
}

/** Copy shown in the tooltip when hovering a locked chip. */
export const lockedTierCopy = (attempted: AiTierId, planName: string): string => {
  if (attempted === 'line_max') {
    return `Line Max is included with ${planName || 'Full Unlimited'}. Upgrade to draft with premium reasoning.`
  }
  if (attempted === 'line_pro') {
    return `Line Pro is included with ${planName || 'Basic Unlimited'} and above.`
  }
  return 'This tier is not available on your current plan.'
}
